import { CampaignPreset } from 'src/api/campaignPresets/types';
import { mapPresetToFormValues } from 'src/modules/campaignPresets/helpers/mapPresetToFormValues';
import { CampaignConfigSchema } from '../types';
import { CampaignFormValues, FormMessage } from '../types';
import { newClientKey } from '../helpers/clientKey';
import {
  getGoverningMessageTemplate,
  normalizeLinksWithMimeType,
  resolveMessageType,
} from './messageHelpers';
import { assignEnrollmentSections } from './enrollmentHelpers';
import { normalizeRecurringMessages } from './recurring';

/**
 * A message as it was frozen into a preset. `buildPresetContent` stores the raw
 * builder message minus server/runtime fields, so every field is optional here.
 */
type PresetMessage = Partial<FormMessage> & Record<string, unknown>;

/**
 * Overlay a saved preset's content onto the policy's fresh-campaign defaults.
 *
 * The stored content wins, but messages are re-normalized so they behave like
 * ones loaded from a campaign, and positions are re-derived from array order so
 * a preset saved with gaps cannot break scheduling. The name is not taken from
 * here — the caller derives a unique one from preset.name.
 */
export function applyPresetToDefaults(
  defaults: CampaignFormValues,
  preset: CampaignPreset,
  config: CampaignConfigSchema,
): CampaignFormValues {
  const mapped = mapPresetToFormValues(preset) as Partial<CampaignFormValues>;
  const presetMessages = (Array.isArray(mapped.scheduledMessages)
    ? mapped.scheduledMessages
    : []) as PresetMessage[];

  const scheduledMessages: FormMessage[] = presetMessages.map((message, index) => {
    const template = getGoverningMessageTemplate(config.messageTemplates, index);
    const fallback = defaults.scheduledMessages[index] ?? defaults.scheduledMessages[0];

    return {
      ...fallback,
      ...message,
      clientKey: newClientKey(),
      type: resolveMessageType(template, message.type),
      text: message.text ?? '',
      subject: message.subject ?? '',
      links: message.links ?? [],
      linksWithMimeType: normalizeLinksWithMimeType(message),
      position: index + 1,
      isCaptureContact: !!message.dataCapture,
    } as FormMessage;
  });

  // A preset that stored no messages falls back to the policy's own fresh-campaign
  // messages, untouched: they already carry the enrollment sections and schedules
  // the policy built them with. The old builder showed a blank immediate MMS here
  // regardless of type, which was a `||`/`?:` precedence bug, not a choice; it died
  // with that module.
  if (!scheduledMessages.length) {
    return {
      ...defaults,
      ...mapped,
      scheduledMessages: defaults.scheduledMessages.map(message => ({
        ...message,
        clientKey: newClientKey(),
      })),
    };
  }

  let normalizedMessages = scheduledMessages;

  if (config.type === 'enrollment') {
    normalizedMessages = assignEnrollmentSections(normalizedMessages.map(message =>
      message.position === 1
        ? { ...message, scheduledType: 'immediate', delay: null, time: null }
        : message,
    ));
  }
  if (config.type === 'recurring') {
    normalizedMessages = normalizeRecurringMessages(
      normalizedMessages,
      () => defaults.scheduledMessages[0],
    );
  }

  return {
    ...defaults,
    ...mapped,
    scheduledMessages: normalizedMessages,
  };
}
